import { useState } from "react";
import { InputField } from "../../../components/forms/InputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateQuestionnaireDate } from "../utils/questionnaire";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { ErrorWrapper, InputWrapper } from "./SharedFormStyles";
import { QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT } from "../../../components/theme";
import useGetCurrentUserDocument from "../../../hooks/useGetCurrentUserDocument";
import { SevenDayRulePopup } from "../utils/SevenDayRulePopup";

interface DateFieldInputProps {
  question: QuestionWithRelations;
}

const DateFieldInput: React.FC<DateFieldInputProps> = ({ question }) => {
  const [dateInput, setDateInput] = useState<string | null>(
    question.answers[0]?.valueTyped
      ? String(question.answers[0].valueTyped)
      : null,
  );
  const [dateError, setDateError] = useState<string | null>(null);

  const { user, document } = useGetCurrentUserDocument();
  const [
    createAnswer,
    {
      /*isLoading: isCreating,*/
      isError: isCreateError,
      /*isSuccess: isCreateSuccess,*/
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      /*isLoading: isUpdating,*/
      isError: isUpdateError,
      /*isSuccess: isUpdateSuccess,*/
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = (value: string | null) => {
    if (!user) return;

    if (!hasAnswer && !value) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateInput(value);
  };

  const handleDateBlur = () => {
    resetCreateAnswer();
    resetUpdateAnswer();

    const error = validateQuestionnaireDate(
      dateInput ?? "",
      question,
      document?.state.abbreviation ?? "",
    );

    setDateError(error);
    if (error) {
      save(null);
      return;
    }
    save(dateInput);
  };

  //const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  /*const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !dateError &&
    (dateInput != null || !question.isRequired);
  */
  return (
    <>
      {dateError?.match(/CA/) && (
        <SevenDayRulePopup
          onConfirm={() => {
            // Reset mutation error states to prevent lingering API errors from showing
            resetCreateAnswer();
            resetUpdateAnswer();
            setDateInput(null);
            setDateError(null);
          }}
        />
      )}
      <InputWrapper>
        <InputField
          type="date"
          placeholder={QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT}
          value={dateInput ?? ""}
          onChange={(e) => {
            handleDateChange(e);
          }}
          onBlur={handleDateBlur}
          required={question.isRequired}
          errorMessage={dateError}
        />
        {/* Commenting out the loader and success icon for now as per the request of the design team */}
        {/*
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
        */}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default DateFieldInput;
